import {
  createFileRoute,
  redirect,
  useNavigate,
  useSearch,
} from "@tanstack/react-router";
import { useEffect } from "react";
import { getDefaultStore } from "jotai";
import { ShieldCheckIcon } from "lucide-react";
import { z } from "zod";
import { AuthPage, RegisterForm } from "@tubo/ui/components/auth";
import { isAuthenticatedAtom, useAuthActions } from "@tubo/store";
import {
  useSysGetBeianNumbers,
  useSysGetRegistrationStatus,
} from "@tubo/api-models/hooks/system-config-api.hooks";
import type { LoginResp } from "@tubo/api-models/hooks/auth-api.schemas";
import { buildBeianSubFooterLinks } from "@client/lib/beian-links";
import { getLoginNavigationOptions } from "@client/lib/login-default-route";

const branding = {
  logo: "/TuboData-Logo-Light.png",
  companyName: "图博数智 inFab",
  tagline: "国内首家依托业务本体建模\n支持本地化交付的产业级 AI 知识库平台",
  brandingBackground: "linear-gradient(150deg, #001a33 0%, #003366 35%, #0055aa 65%, #007aff 100%)",
  footerText: `© ${new Date().getFullYear()} 图博数智 inFab. All rights reserved.`,
  footerLinksOnBranding: false,
  footerLinks: [
    {
      label: "隐私协议",
      href: "/privacy-policy",
      icon: <ShieldCheckIcon className="size-3.5" aria-hidden="true" />,
    },
  ],
};

const registerSearchSchema = z.object({
  redirect: z.string().optional(),
});

export const Route = createFileRoute("/register")({
  validateSearch: registerSearchSchema,
  beforeLoad: ({ search }) => {
    if (getDefaultStore().get(isAuthenticatedAtom)) {
      throw redirect(getLoginNavigationOptions(search.redirect ?? "/"));
    }
  },
  component: RegisterPage,
});

function RegisterPage() {
  const navigate = useNavigate();
  const { redirect } = useSearch({ from: "/register" });
  const { setAuth } = useAuthActions();
  const { data: registrationData } = useSysGetRegistrationStatus();
  const registrationDisabled = registrationData?.allowed === false;
  const { data: beianData } = useSysGetBeianNumbers();
  const subFooterLinks = buildBeianSubFooterLinks(beianData);

  // 自助注册已关闭：直达 /register 时回退登录页
  useEffect(() => {
    if (registrationDisabled) {
      navigate({ to: "/login", search: { redirect }, replace: true });
    }
  }, [registrationDisabled, navigate, redirect]);

  if (registrationDisabled) {
    return null;
  }

  const handleSuccess = async (resp: LoginResp) => {
    // 设置密码成功后（无 token），跳转登录页
    if (!resp.token?.accessToken) {
      navigate({ to: "/login", search: { redirect } });
      return;
    }
    setAuth({ token: resp.token.accessToken, me: resp.me });
    // 身份边界 = 运行时边界：整页进入目标路由，上一会话的内存态
    // （Query 缓存/模块级 atom/blob 缓存等）随页面卸载全部蒸发
    window.location.assign(redirect ?? "/");
  };

  return (
    <AuthPage branding={{ ...branding, subFooterLinks }}>
      <RegisterForm
        onSuccess={handleSuccess}
        onToggleMode={() => navigate({ to: "/login", search: { redirect } })}
      />
    </AuthPage>
  );
}
